import React, { useState, useEffect } from "react"
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts"
import { Printer, ChevronDown, GraduationCap, Target, BookOpen, Award, FileText } from "lucide-react"
import { Sidebar } from "@/components/ui/sidebar"
import ProtectedRoute from "@/components/ProtectedRoute"
import { buildStudentSummary } from "@/hooks/useStudentSummary"
import { useStudentAnalytics } from "@/hooks/useStudentAnalytics"
import { generateReport } from "@/hooks/useReportGenerator"
import { ReportModal } from "@/components/ui/report/report-modal"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { getMyChildren } from "@/hooks/api/schoolApi"
import config from "../../config"

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLinkedStudents(): any[] {
  const user = AuthStore.getState().currentUser
  if (!user) return []
  const students: any[] = StorageService.read('students')
  // Parent is linked via student.parentIds or student.parentPortalAccess.parentEmails
  return students.filter((s) =>
    s.parentIds?.includes(user._id) ||
    s.parentPortalAccess?.parentEmails?.includes(user.email)
  )
}

function StatCard({ label, value, icon: Icon, color }: {
  label: string; value: string | number; icon: React.ElementType; color: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-black text-slate-900 leading-none">{value}</p>
        <p className="text-xs font-semibold text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

// ─── Print report ─────────────────────────────────────────────────────────────
function handlePrint() {
  window.print()
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ParentPortalPage() {
  const [apiChildren, setApiChildren] = useState<any[]>([])
  const localStudents = getLinkedStudents()

  useEffect(() => {
    if (config.appMode === "prototype") return
    getMyChildren()
      .then(({ children }) => { if (children?.length) setApiChildren(children) })
      .catch(() => {})
  }, [])

  const linkedStudents = apiChildren.length > 0 ? apiChildren : localStudents
  const [selectedId, setSelectedId] = useState<string>("")
  const resolvedId = selectedId || linkedStudents[0]?._id || ""

  const student = linkedStudents.find((s) => s._id === resolvedId) ?? linkedStudents[0]
  const summary = student ? buildStudentSummary(student) : null
  const analytics = useStudentAnalytics(student?.userId ?? student?._id ?? "")
  const [report, setReport] = useState<ReturnType<typeof generateReport> | null>(null)

  const radarData = summary?.topTraits.map((t) => ({ subject: t.name, score: t.score })) ?? []

  const handleGenerateReport = () => {
    if (!summary) return
    setReport(generateReport(summary, analytics, 'parent', student?.gender))
  }

  if (!linkedStudents.length) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-100">
          <Sidebar />
          <main className="flex-1 lg:ml-64 flex items-center justify-center p-6">
            <div className="text-center space-y-3">
              <GraduationCap className="w-12 h-12 text-slate-300 mx-auto" />
              <h2 className="text-xl font-bold text-slate-700">No students linked</h2>
              <p className="text-sm text-slate-500 max-w-sm">
                Ask your child's school admin to link your parent account to your child's profile.
              </p>
            </div>
          </main>
        </div>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />

        <main className="flex-1 lg:ml-64 p-4 md:p-6 overflow-y-auto print:ml-0 print:p-4">
          <div className="max-w-5xl mx-auto space-y-5">

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-black text-slate-900">Parent Portal</h1>
                <p className="text-sm text-slate-500 mt-0.5">Read-only view of your child's learning progress.</p>
              </div>
              <div className="flex items-center gap-3">
                {linkedStudents.length > 1 && (
                  <div className="relative">
                    <select
                      value={resolvedId}
                      onChange={(e) => setSelectedId(e.target.value)}
                      className="appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2 pr-8 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {linkedStudents.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.firstName} {s.lastName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                )}
                <button
                  onClick={handleGenerateReport}
                  disabled={!summary}
                  className="flex items-center gap-2 bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 text-sm font-bold px-4 py-2 rounded-xl transition-all active:scale-95 print:hidden"
                >
                  <FileText className="w-4 h-4" />
                  Written Report
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-all active:scale-95 print:hidden"
                >
                  <Printer className="w-4 h-4" />
                  Print Charts
                </button>
              </div>
            </div>

            {/* Child profile strip */}
            {summary && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-2xl font-black text-indigo-600 shrink-0">
                  {summary.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 text-lg leading-tight">{summary.name}</p>
                  <p className="text-sm text-slate-500">
                    {summary.classLevel} — {summary.examBody} — {summary.country}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Interests</p>
                  <p className="text-sm text-slate-700 font-medium">{summary.careerInterests.slice(0, 2).join(', ') || '—'}</p>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Active Goals" value={analytics.goalProgress.active} icon={Target} color="bg-teal-100 text-teal-600" />
              <StatCard label="Goals Completed" value={analytics.goalProgress.completed} icon={Award} color="bg-amber-100 text-amber-600" />
              <StatCard label="Overdue Goals" value={analytics.goalProgress.overdue} icon={Target} color="bg-red-100 text-red-500" />
              <StatCard label="Study Sessions" value={analytics.engagementTrend.reduce((s, e) => s + e.sessions, 0)} icon={BookOpen} color="bg-indigo-100 text-indigo-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Trait radar */}
              <Section title="Trait Profile">
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Radar name="Score" dataKey="score" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-10">Psychometric assessment not yet completed.</p>
                )}
              </Section>

              {/* Weekly engagement */}
              <Section title="Study Sessions per Week">
                {analytics.engagementTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analytics.engagementTrend} barSize={20}>
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-10">No session data yet.</p>
                )}
              </Section>
            </div>

            {/* Career path progress */}
            {analytics.careerPathProgress.length > 0 && (
              <Section title="Career Path Progress">
                <div className="space-y-3">
                  {analytics.careerPathProgress.map((p) => (
                    <div key={p.pathId}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold text-slate-700">{p.pathName}</span>
                        <span className="font-bold text-indigo-600">{p.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5">
                        <div
                          className="bg-linear-to-r from-indigo-500 to-violet-500 h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${p.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Recent activity */}
            {summary && summary.recentActivity.length > 0 && (
              <Section title="Recent Activity (Last 5)">
                <ul className="space-y-2">
                  {summary.recentActivity.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

          </div>
        </main>
      </div>

      {report && <ReportModal report={report} onClose={() => setReport(null)} />}
    </ProtectedRoute>
  )
}
